<div class="space-y-12">
  @if (!hideStepHeader()) {
    <div class="max-w-3xl">
      <h2 class="text-3xl font-bold tracking-tight text-neutral-12">
        Construction et caractéristiques du bâtiment
      </h2>
      <p class="mt-2 text-lg text-neutral-11">
        Ces détails techniques valorisent la structure et les équipements de votre bien.
      </p>
    </div>
  }

  <div class="space-y-8">
    <!-- Construction et standing -->
    <div class="rounded-3xl border border-neutral-3 bg-neutral-1 p-6 md:p-10">
      <div class="mb-6 flex flex-col gap-1">
        <h3 class="text-xl font-bold text-neutral-12">Construction et standing</h3>
        <p class="text-sm text-neutral-10">Informations structurelles du bâtiment et niveau de prestation.</p>
      </div>

      <div class="flex flex-col gap-6">
        <!-- Données du bâtiment issues de la BDNB -->
        <div class="flex flex-col gap-4 rounded-2xl border border-neutral-3 bg-neutral-2/40 p-5">
          <div class="flex items-center justify-between">
            <div class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-neutral-12">Données du bâti</span>
              <span class="text-xs text-neutral-10"
                >Renseigné automatiquement via le registre national des bâtiments (BDNB)</span
              >
            </div>
            <span
              class="inline-flex items-center gap-1.5 rounded-full border border-neutral-4 bg-neutral-3 px-2.5 py-1 text-xs font-medium text-neutral-11"
            >
              <mat-icon class="!h-3.5 !w-3.5 !text-sm leading-none text-neutral-10">apartment</mat-icon>
              Registre BDNB
            </span>
          </div>

          <div class="grid grid-cols-1 gap-4 border-t border-neutral-3 pt-4 md:grid-cols-3">
            <!-- Période de construction -->
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-neutral-11">Période de construction</span>
              <div>
                @if (model().year) {
                  <span
                    class="inline-flex items-center rounded-lg border border-neutral-4 bg-white px-3 py-1 text-xs font-semibold text-neutral-12 shadow-xs dark:bg-neutral-3"
                  >
                    {{ model().year | etxeEnumDisplay: 'constructionPeriod' }}
                  </span>
                } @else {
                  <span class="text-xs italic text-neutral-10">Non renseignée</span>
                }
              </div>
            </div>

            <!-- Matériaux des murs -->
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-neutral-11">Matériaux des murs</span>
              <div>
                @if (model().material.walls.length > 0) {
                  <div class="flex flex-wrap gap-1.5">
                    @for (mat of model().material.walls; track mat) {
                      <span
                        class="inline-flex items-center rounded-lg border border-neutral-4 bg-white px-2.5 py-1 text-xs font-semibold text-neutral-12 shadow-xs dark:bg-neutral-3"
                      >
                        {{ mat | etxeEnumDisplay: 'material' }}
                      </span>
                    }
                  </div>
                } @else {
                  <span class="text-xs italic text-neutral-10">Non renseignés</span>
                }
              </div>
            </div>

            <!-- Matériaux de toiture -->
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-neutral-11">Matériaux de toiture</span>
              <div>
                @if (model().material.roof.length > 0) {
                  <div class="flex flex-wrap gap-1.5">
                    @for (mat of model().material.roof; track mat) {
                      <span
                        class="inline-flex items-center rounded-lg border border-neutral-4 bg-white px-2.5 py-1 text-xs font-semibold text-neutral-12 shadow-xs dark:bg-neutral-3"
                      >
                        {{ mat | etxeEnumDisplay: 'roofMaterial' }}
                      </span>
                    }
                  </div>
                } @else {
                  <span class="text-xs italic text-neutral-10">Non renseignés</span>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Standing du bien -->
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>Standing du bien</mat-label>
          <mat-select [formField]="formTree.standing">
            @for (standing of propertyStandingTypes; track standing) {
              <mat-option [value]="standing">{{ standing | etxeEnumDisplay: 'standing' }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>
    </div>

    <!-- Property Condition -->
    <div class="rounded-3xl border border-neutral-3 bg-neutral-1 p-6 md:p-10">
      <h3 class="mb-6 text-xl font-bold text-neutral-12">État du bien</h3>

      <div class="grid grid-cols-1 gap-6" [class.md:grid-cols-2]="!isHouse()">
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>État intérieur</mat-label>
          <mat-select [formField]="formTree.conditions.interior">
            @for (condition of propertyConditionTypes; track condition) {
              <mat-option [value]="condition">{{ condition | etxeEnumDisplay: 'condition' }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        @if (!isHouse()) {
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>État des parties communes</mat-label>
            <mat-select [formField]="formTree.conditions.commonAreas">
              @for (condition of propertyConditionTypes; track condition) {
                <mat-option [value]="condition">{{ condition | etxeEnumDisplay: 'condition' }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }
      </div>
    </div>

    <!-- Technical Characteristics -->
    <div class="rounded-3xl border border-neutral-3 bg-neutral-1 p-6 md:p-10">
      <div class="mb-6 flex flex-col gap-1">
        <h3 class="text-xl font-bold text-neutral-12">Caractéristiques techniques</h3>
        <p class="text-sm text-neutral-10">Dimensions et orientation de votre logement.</p>
      </div>

      <div class="flex flex-col gap-6">
        <!-- Données de dimensions issues de la BDNB -->
        <div class="flex flex-col gap-3 rounded-2xl border border-neutral-3 bg-neutral-2/40 p-5">
          <div class="flex items-center justify-between">
            <div class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-neutral-12">Dimensions du bâti</span>
              <span class="text-xs text-neutral-10"
                >Renseigné automatiquement via le registre national des bâtiments (BDNB)</span
              >
            </div>
            <span
              class="inline-flex items-center gap-1.5 rounded-full border border-neutral-4 bg-neutral-3 px-2.5 py-1 text-xs font-medium text-neutral-11"
            >
              <mat-icon class="!h-3.5 !w-3.5 !text-sm leading-none text-neutral-10">straighten</mat-icon>
              Registre BDNB
            </span>
          </div>

          <div class="grid grid-cols-1 gap-4 border-t border-neutral-3 pt-3 sm:grid-cols-2">
            <!-- Hauteur du bâtiment -->
            <div class="flex items-center justify-between">
              <div class="flex flex-col gap-0.5">
                <span class="text-sm font-medium text-neutral-11">Hauteur du bâtiment</span>
                <span class="text-xs text-neutral-9">Hauteur estimée</span>
              </div>
              <div>
                @if (model().buildingHeight) {
                  <span
                    class="inline-flex items-center rounded-lg border border-neutral-4 bg-white px-3 py-1 text-sm font-semibold text-neutral-12 shadow-xs dark:bg-neutral-3"
                  >
                    {{ model().buildingHeight }} m
                  </span>
                } @else {
                  <span class="text-xs italic text-neutral-10">Non renseignée</span>
                }
              </div>
            </div>

            <!-- Emprise au sol -->
            <div class="flex items-center justify-between">
              <div class="flex flex-col gap-0.5">
                <span class="text-sm font-medium text-neutral-11">Emprise au sol</span>
                <span class="text-xs text-neutral-9">Emprise au sol du bâti</span>
              </div>
              <div>
                @if (model().groundFootprint) {
                  <span
                    class="inline-flex items-center rounded-lg border border-neutral-4 bg-white px-3 py-1 text-sm font-semibold text-neutral-12 shadow-xs dark:bg-neutral-3"
                  >
                    {{ model().groundFootprint }} m²
                  </span>
                } @else {
                  <span class="text-xs italic text-neutral-10">Non renseignée</span>
                }
              </div>
            </div>

            <!-- Hauteur sous plafond -->
            @if (model().ceilingHeight) {
              <div class="flex items-center justify-between border-t border-neutral-3/60 pt-3 sm:col-span-2">
                <div class="flex flex-col gap-0.5">
                  <span class="text-sm font-medium text-neutral-11">Hauteur sous plafond</span>
                  <span class="text-xs text-neutral-9">Relevée lors du diagnostic énergétique</span>
                </div>
                <div>
                  <span
                    class="inline-flex items-center rounded-lg border border-neutral-4 bg-white px-3 py-1 text-sm font-semibold text-neutral-12 shadow-xs dark:bg-neutral-3"
                  >
                    {{ model().ceilingHeight }} m
                  </span>
                </div>
              </div>
            }
          </div>
        </div>

        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <!-- Openings -->
          <aura-number-input
            label="Nombre d'ouvertures"
            [value]="model().openings ?? undefined"
            (valueChange)="model.update((m) => ({ ...m, openings: $event ?? null }))"
            description="Portes et fenêtres extérieures"
          ></aura-number-input>

          <!-- Orientation -->
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Orientation</mat-label>
            <mat-select [formField]="formTree.orientation" multiple>
              @for (orientation of orientationTypes; track orientation) {
                <mat-option [value]="orientation">{{
                  orientation | etxeEnumDisplay: 'orientation'
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <!-- Exposure -->
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Ensoleillement et exposition</mat-label>
            <mat-select [formField]="formTree.exposure">
              <mat-option [value]="null">Non précisé</mat-option>
              @for (type of exposureTypes; track type) {
                <mat-option [value]="type">{{ type | etxeEnumDisplay: 'exposure' }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <!-- View -->
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Vue(s)</mat-label>
            <mat-select [formField]="formTree.view" multiple>
              @for (view of propertyViewTypes; track view) {
                <mat-option [value]="view">{{ view | etxeEnumDisplay: 'view' }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
      </div>
    </div>
  </div>
</div>
